import {
  FileMetadataContext,
  useRecordFileMetadata
} from '@Pages/pageRuntime/context/FileMetadataContext';
import { useResolvedResource } from '@Pages/pageRuntime/context/SdkResourceResolverContext';
import type { RuntimeCollectionResult } from '@Pages/pageRuntime/fileMetadataStore';
import { assertResourceMethodAvailable } from '@Pages/pageRuntime/sdk/endpointGating';
import type {
  ResourceRecord,
  SdkInfiniteRecordsHandle,
  SdkReferenceRecord,
  SdkReferenceRelationPath,
  SdkResourceQueryReference,
  SdkUseInfiniteRecordsOptions
} from '@Pages/pageRuntime/sdk/types';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';

import { buildSearchBody } from './useRecords';

const NO_RECORDS: ResourceRecord[] = [];
const MAX_RETAINED_PAGES = 10;

/**
 * Search-backed infinite record collection exposed through
 * `Sdk.hooks.useInfiniteRecords(resource, options)`.
 *
 * A nullish `resource`, or an id / slug the viewer cannot access, keeps the hook idle
 * instead of throwing.
 */
export function useInfiniteRecords<
  TReference extends SdkResourceQueryReference<'search'>,
  const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
>(
  resource: TReference,
  options?: SdkUseInfiniteRecordsOptions<TRelations>
): SdkInfiniteRecordsHandle<SdkReferenceRecord<TReference, TRelations>>;
export function useInfiniteRecords(
  reference: SdkResourceQueryReference<'search'>,
  options?: SdkUseInfiniteRecordsOptions
): SdkInfiniteRecordsHandle {
  const fileStore = useContext(FileMetadataContext);
  const resource = useResolvedResource(reference);
  const queryClient = useQueryClient();

  const body = buildSearchBody(options);
  const searchUrl = resource && 'urls' in resource ? resource.urls.search : undefined;
  const resourceName = resource?.name ?? '';
  const disabledEndpoints = resource?.disabled_data_api_endpoints ?? [];
  const queryKey = [
    'records',
    resource?.id,
    'infinite',
    body,
    searchUrl,
    resourceName,
    disabledEndpoints,
    fileStore?.scope
  ];

  const query = useInfiniteQuery<RuntimeCollectionResult>({
    enabled: (options?.enabled ?? true) && searchUrl !== undefined,
    queryKey,
    queryFn: ({ pageParam }) => {
      assertResourceMethodAvailable(
        { name: resourceName, disabled_data_api_endpoints: disabledEndpoints },
        'search'
      );

      return axios
        .post(searchUrl as string, { ...body, page: pageParam })
        .then((response) => fileStore?.receive(response.data) ?? response.data);
    },
    initialPageParam: 1,
    maxPages: MAX_RETAINED_PAGES,
    getNextPageParam: (page) =>
      page.pagination.current_page < page.pagination.last_page
        ? page.pagination.current_page + 1
        : undefined
  });

  const pages = query.data?.pages;
  const files = useRecordFileMetadata(pages ?? []);

  return {
    files,
    data: pages?.flatMap((page) => page.data) ?? NO_RECORDS,
    pagination: pages?.at(-1)?.pagination,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => query.fetchNextPage().then(() => undefined),
    invalidate: () => queryClient.invalidateQueries({ queryKey })
  };
}
